/**
 * CACHE ARCHITECTURE (5-layer):
 * ──────────────────────────────
 * Layer 0: TanStack memory cache (in-memory, instant, same session)
 * Layer 1: persistQueryClient (localStorage, instant, 24h max age)
 * Layer 2: Live API (FastAPI backend, background refetch)
 * Layer 3: /fallback/*.json (same server, webhook-updated)
 * Layer 4: Hardcoded placeholderData (in JS bundle, always available)
 *
 * FLOW ON REFRESH:
 *   1. Service Worker serves cached JS/CSS/images instantly
 *   2. persistQueryClient hydrates from localStorage → render instantly
 *   3. API call fires in background → updates if changed (no flicker)
 *   4. If API fails → /fallback/*.json loads → persists to localStorage
 *   5. If fallback missing → hardcoded placeholderData shows (in JS bundle)
 *
 * This file implements Layers 2-4. See queryClient.ts for Layers 0-1.
 */

/**
 * Generic site setting hook.
 *
 * ARCHITECTURE:
 *   Layer 0: Memory cache (current session)
 *   Layer 1: persistQueryClient (localStorage) - returning visitors see cache instantly
 *   Layer 2: Live API → JSON fallback (if API fails)
 *   Layer 3: Hardcoded fallback (always available, built into JS bundle)
 *
 * FALLBACK GUARANTEE:
 *   Always returns hardcoded fallback as `data` when no real data is available.
 */
import { useQuery } from "@tanstack/react-query";
import { getSiteSetting } from "@/exxonim/services/siteSettingsService";
import { fetchWithPublicSnapshotFallback } from "@/exxonim/services/staticFallbackService";
import { getFallbackSiteSetting } from "@/exxonim/content/fallbackPublicContent";
import type { SiteSetting } from '@/exxonim/types';

export function useSiteSetting<TValue = unknown>(key: string) {
  const fallback = getFallbackSiteSetting(key) as SiteSetting<TValue> | undefined;

  const query = useQuery({
    queryKey: ["site-settings", key],
    queryFn: () =>
      fetchWithPublicSnapshotFallback(
        () => getSiteSetting<TValue>(key),
        "site-settings",
        `site-settings-${key}`
      ),
    placeholderData: fallback,
    staleTime: 1000 * 30, // 30 seconds - generic setting hook
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } } | null)?.response?.status;
      if (status === 404) return false;
      return failureCount < 2;
    },
  });

  return {
    ...query,
    data: query.data ?? fallback,
    isPending: query.isPending && !fallback,
  };
}
